import React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { DeckStore, CardStore, UserStore } from '@seedlang/stores';
import Copy from 'components/copy';
import autobind from 'autobind-decorator';
import Spinner from 'components/spinner';
import { computed, observable } from 'mobx';
import Paginator from 'components/paginator';
import SortableColumns from 'components/hoc/sortable_columns';
import cx from 'classnames';
import { Link } from 'react-router';
import Filter from 'components/backend_filter/filter';
import { Constants } from '@seedlang/constants';
import Modal from 'components/modal';
import ReviewDetails from 'components/card/review_details';

const Wrapper = styled.div`
  .table-container {
    overflow-x: auto;
    width: 100%;
  }

  .table-wrapper {
    min-width: 1200px;
    
    th {
      white-space: nowrap;
    }
  }
`;

@observer
class ReviewsIndex extends React.Component {
  @observable isLoading = true;
  @observable selectedCard = null;
  @observable showDetailsModal = false;

  constructor(props) {
    super(props);
    this.loadUser();
    this.loadDeck();
  }

  @computed get queryStrings() {
    return {
      deck_id: this.props.params.deckId,
      user_id: this.props.params.userId,
      include_count: true,
    };
  }

  @autobind loadUser() {
    if (!UserStore.showData || UserStore.showData.id !== this.props.params.userId) {
      UserStore.getShow({ids: {userId: this.props.params.userId}});
    }
  }

  @autobind loadDeck() {
    if (!DeckStore.indexData || !DeckStore.indexData.find(d => d.id == this.props.params.deckId)) {
      DeckStore.getIndex({queryStrings: {user_id: this.props.params.userId}, limit: 5});
    }
  }

  @computed get cards() {
    return CardStore.indexData || [];
  }

  @computed get deckName() {
    const deck = DeckStore.indexData.find(d => d.id == this.props.params.deckId);
    return deck ? `${deck.name} (${deck.languageId})` : 'Loading...';
  }

  getCardText(card, side) {
    if (side === 'front') {
      if (card.frontText) return card.frontText;
      if (card.word?.targetTextWithDefiniteArticle) return card.word.targetTextWithDefiniteArticle;
      if (card.sentence?.targetText) return card.sentence.targetText;
      return 'No front text';
    } else {
      if (card.backText) return card.backText;
      if (card.word?.vocabSourceList) return card.word.vocabSourceList;
      if (card.sentence?.sourceText) return card.sentence.sourceText;
      return '';
    }
  }

  truncateText(text, maxLength = 25) {
    if (!text) return '-';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  }

  @autobind openDetailsModal(card) {
    this.selectedCard = card;
    this.showDetailsModal = true;
  }

  @autobind closeDetailsModal() {
    this.showDetailsModal = false;
    this.selectedCard = null;
  }

  @autobind onSortColumn(column) {
    this.props.onSortColumn(column, false, { queryStrings: this.queryStrings });
  }

  render() {
    const loading = CardStore.requestCounter > 0 || DeckStore.requestCounter > 0;

    return (
      <Wrapper>
        <div className='reviews-index'>
          <div className='breadcrumbs-wrapper'>
            <div className='breadcrumbs'>
              <Link to={{ name: 'builder.users.index' }}>
                Users
              </Link>
              <i className='fa fa-angle-double-right' />
              <Link to={{ name: 'builder.users.edit', params: { userId: this.props.params.userId } }}>
                {UserStore.showData.name || '...'}
              </Link>
              <i className='fa fa-angle-double-right' />
              <span>Review Cards - {this.deckName}</span>
            </div>
          </div>

          <Filter
            store={CardStore}
            defaultSort='-created_at'
            namespace='reviews-index'
            queryStrings={this.queryStrings}
            filters={[
              {
                type: 'select',
                name: 'card_type_id',
                label: 'Card Type',
                options: Constants.REVIEW_CARD_TYPES.map(type => ({ id: type.id, name: type.name })),
                labelField: 'name',
                default: '',
              },
              {
                type: 'date',
                name: 'created_at',
                label: 'Created Date',
                default: '',
              },
              {
                type: 'date',
                name: 'review_after',
                label: 'Next Due Date',
                default: '',
              },
              {
                type: 'boolean',
                name: 'retired',
                label: 'Retired',
              },
            ]}
          />

          {loading && <Spinner />}

          {
            !loading && this.cards.length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                No review cards found for this deck and user.
              </div>
            )
          }

          {
            !loading && this.cards.length > 0 && (
              <>
                <div className='table-container'>
                  <table className='table-wrapper'>
                  <thead>
                    <tr>
                      <th>Card ID</th>
                      <th width='100'>Reviews</th>
                      <th>Front Text</th>
                      <th>Back Text</th>
                      <th>Type</th>
                      <th
                        className={cx('sortable', this.props.columnClassNames('difficulty_score'))}
                        onClick={() => this.onSortColumn('difficulty_score')}
                        width='60'
                      >
                        Difficulty
                      </th>
                      <th
                        className={cx('sortable', this.props.columnClassNames('review_after'))}
                        onClick={() => this.onSortColumn('review_after')}
                      >
                        Next Due
                      </th>
                      <th
                        className={cx('sortable', this.props.columnClassNames('created_at'))}
                        onClick={() => this.onSortColumn('created_at')}
                      >
                        Created
                      </th>
                      <th
                        className={cx('sortable', this.props.columnClassNames('reviews_count'))}
                        onClick={() => this.onSortColumn('reviews_count')}
                        width='50'
                      >
                        # Reviews
                      </th>
                      <th width='60'>Status</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {this.cards.map(card => (
                      <tr key={card.id}>
                        <td>
                          <Copy text={card.id} />
                        </td>
                        <td>
                          <button
                            className='btn btn-sm btn-primary'
                            onClick={() => this.openDetailsModal(card)}
                          >
                            Reviews
                          </button>
                        </td>
                        <td>
                          <div className='text' title={this.getCardText(card, 'front')}>
                            {this.truncateText(this.getCardText(card, 'front'))}
                          </div>
                        </td>
                        <td>
                          <div className='text' title={this.getCardText(card, 'back')}>
                            {this.truncateText(this.getCardText(card, 'back'))}
                          </div>
                        </td>
                        <td>
                          <div className='text'>
                            {Constants.REVIEW_CARD_TYPES.find(type => type.id === card.cardTypeId)?.name || 'Unknown'}
                          </div>
                        </td>
                        <td>
                          <div className='text'>
                            {card.difficultyScore || 0}
                          </div>
                        </td>
                        <td>
                          <div className='text'>
                            {card.reviewAfter?.formattedDateWithYear || 'Never'}
                          </div>
                        </td>
                        <td>
                          <div className='text'>
                            {card.createdAt?.formatted || 'Never'}
                          </div>
                        </td>
                        <td>
                          <div className='text'>
                            {card.reviewsCount || 0}
                          </div>
                        </td>
                        <td>
                          <div className='text'>
                            {card.retired ? 'Retired' : 'Active'}
                          </div>
                        </td>
                        <td>
                          <div className='text' title={card.note || ''}>
                            {this.truncateText(card.note)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>

                <Paginator
                  store={CardStore}
                  queryStrings={this.queryStrings}
                  limit={20}
                />
              </>
            )
          }

          {
            this.showDetailsModal && this.selectedCard && (
              <Modal
                onCloseModal={this.closeDetailsModal}
                animate
                width='800px'
                title='Review Details'
                gradient
              >
                <ReviewDetails card={this.selectedCard} />
              </Modal>
            )
          }
        </div>
      </Wrapper>
    );
  }
}

export default SortableColumns(ReviewsIndex, CardStore, { sortColumn: 'created_at', sortIsAscending: false });